import { useEffect, useRef } from "react"
import { QueryQueue, SupersededRunError } from "features/Dashboard/runtime/queryQueue"
import type { ValueRange } from "../types/catalogue"
import { EngineTimeoutError } from "./engineTimeout"
import { useTabSpecStore, useTabSpecStoreApi } from "./tabSpecStore"
import {
  readValueCheck,
  unreadValuePaths,
  valueCheckNames,
  valueCheckScript,
} from "./valueCheck"

/**
 * Runs the values check in the session once it is ready, through the
 * session's queue: one request for the fields the tables name that it has
 * not read, and another when a table names more. What it finds goes to the
 * store for this session only (`setValueRanges`). A request that fails
 * gives "failed" for its fields, so their tables say so instead of waiting;
 * one a new session overtook gives nothing, and the new session asks again.
 */
export const useValueCheck = (queue: QueryQueue | null): void => {
  const store = useTabSpecStoreApi()
  const ready = useTabSpecStore((s) => s.session.status === "ready")
  const generation = useTabSpecStore((s) => s.session.generation)
  // One string, so an edit that changes none of the fields renders nothing.
  // It only says when to look: the fields are read from the store below.
  const wanted = useTabSpecStore((s) => unreadValuePaths(s).join("\n"))
  /** The fields asked for in session `generation`: read, or on their way. */
  const asked = useRef({ generation: -1, paths: new Set<string>() })

  useEffect(() => {
    if (!queue || !ready || !wanted) return
    if (asked.current.generation !== generation) asked.current = { generation, paths: new Set() }
    const own = asked.current.paths
    // The fields as the store has them now, not as this render saw them: an
    // effect of the same commit may already have put the catalogue of a new
    // preparation there, and a field it no longer has would fail the request.
    const paths = unreadValuePaths(store.getState()).filter((p) => !own.has(p))
    if (paths.length === 0) return
    paths.forEach((p) => own.add(p))
    void (async () => {
      let found: Record<string, ValueRange>
      try {
        const result = await queue.runPlain(valueCheckScript(paths))
        found = readValueCheck(paths, result.result?.output ?? [], store.getState().codebook)
      } catch (e) {
        if (e instanceof SupersededRunError) return
        // The engine stops at a failing line: a name made before it stays.
        // After a timeout a REMOVE would wait as long; the next RESET removes it.
        if (!(e instanceof EngineTimeoutError))
          for (const name of valueCheckNames(paths)) {
            try {
              await queue.runPlain(`REMOVE ${name}`)
            } catch {
              // Never made, or already gone.
            }
          }
        found = Object.fromEntries(paths.map((p) => [p, "failed" as const]))
      }
      store.getState().setValueRanges(generation, found)
    })()
  }, [queue, ready, generation, wanted, store])
}
